import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Admin.css';
import './ElevenLabsAgents.css';
import './CurrentAgentControl.css';

function ElevenLabsAgents() {
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });
  const [currentAgentId, setCurrentAgentId] = useState(null);

  // Fetch agents from our database
  const fetchAgents = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/admin/elevenlabs-agents', {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch ElevenLabs agents');
      }
      
      const data = await response.json();
      setAgents(data);
      setError('');
      
      // Fetch current agent
      await fetchCurrentAgent();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch current agent ID
  const fetchCurrentAgent = async () => {
    try {
      const response = await fetch('/api/admin/elevenlabs-current-agent', {
        credentials: 'include'
      });
      
      if (!response.ok) {
        return; // Don't throw error, just handle quietly
      }
      
      const data = await response.json();
      if (data.currentAgent) {
        setCurrentAgentId(data.currentAgent.elevenlabs_convai_agent_id);
      } else {
        setCurrentAgentId(null);
      }
    } catch (err) {
      console.error('Error fetching current agent:', err);
      // Don't show error to user for this operation
    }
  };

  // Refresh agents data in the database
  const refreshAgents = async () => {
    try {
      setIsRefreshing(true);
      setSuccessMessage('');
      setError('');
      
      // Call our server endpoint to refresh agents
      const response = await fetch('/api/admin/elevenlabs-agents/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to refresh agents');
      }
      
      const data = await response.json();
      
      // Now fetch the updated agents from our database
      await fetchAgents();
      
      setSuccessMessage(`Agents refreshed successfully. ${data.agentsCount || 'All'} agents updated.`);
    } catch (err) {
      setError(`Error refreshing agents: ${err.message}`);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Sort agents
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Format date to ISO 8601 without timezone
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toISOString().slice(0, 19).replace('T', ' ');
  };

  // Get sorted agents
  const getSortedAgents = () => {
    const sortableAgents = [...agents];
    if (sortConfig.key) {
      sortableAgents.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableAgents;
  };

  // Fetch agents on initial load
  useEffect(() => {
    fetchAgents();
  }, []);

  return (
    <div>
      <div className="refresh-controls">
        <h3>ElevenLabs Agents</h3>
        <div className="button-group">
          <a 
            href="https://elevenlabs.io/app/conversational-ai/agents"
            target="_blank"
            rel="noopener noreferrer"
            className="elevenlabs-portal-button"
          >
            ElevenLabs ConvAI Portal
          </a>
          <button 
            className="refresh-button" 
            onClick={refreshAgents} 
            disabled={isRefreshing}
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh Agents'}
          </button>
        </div>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}
      
      {isLoading ? (
        <p>Loading agents...</p>
      ) : (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th onClick={() => requestSort('agent_id')}>Agent ID</th>
                <th onClick={() => requestSort('name')}>Name</th>
                <th onClick={() => requestSort('llm_model')}>LLM Model</th>
                <th onClick={() => requestSort('temperature')}>Temperature</th>
                <th onClick={() => requestSort('max_duration_seconds')}>Max Duration (sec)</th>
                <th onClick={() => requestSort('captured_at')}>Last Updated</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {getSortedAgents().length > 0 ? (
                getSortedAgents().map(agent => (
                  <tr key={agent.agent_id}>
                    <td>
                      <Link to={`/admin/agents/${agent.agent_id}`}>
                        {agent.agent_id}
                      </Link>
                    </td>
                    <td>{agent.name}</td>
                    <td>{agent.llm_model || 'N/A'}</td>
                    <td>{agent.temperature !== null ? agent.temperature : 'N/A'}</td>
                    <td>{agent.max_duration_seconds || 'N/A'}</td>
                    <td>{formatDate(agent.captured_at)}</td>
                    <td>
                      {currentAgentId === agent.agent_id && (
                        <span className="current-agent-indicator">Current</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center' }}>No agents found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ElevenLabsAgents;